/* ═══════════════════════════════════════════════════════════════════════
   PipelineBoard — the admin Dashboard's drag-and-drop "Live Pipeline".

   THE CONTRACT THIS MIRRORS
   -------------------------
   shop.advance_order is the authority for what may move where: every status
   change goes through it, it refuses a move the graph does not allow, and it
   knows the ACTOR — a HOME-DELIVERY order can never be pushed to
   out_for_delivery / delivered / delivery_failed / left_at_doorstep (and the
   legacy spellings out_delivery / shipped) from the portal. The rider does
   those legs from the MilliyO Rider app.

   Server TRANSITIONS (copied from shop.CfgOrderTransitions, admin rows,
   not invented — migrations 0031/0032):
     pending          → accepted, cancelled
     accepted         → assigned, cancelled
     assigned         → preparing, cancelled
     preparing        → processing_done, cancelled
     processing_done  → waiting_delivery, ready_pickup
     waiting_delivery → out_for_delivery
     ready_pickup     → completed
     out_for_delivery → delivered, delivery_failed, left_at_doorstep
     delivered        → completed
     left_at_doorstep → completed
     delivery_failed  → could_not_deliver, out_for_delivery
     could_not_deliver→ (terminal)

   TWO THINGS THAT LOOK LIKE RULES AND ARE NOT
     · pending → assigned does NOT exist. The "New" column holds pending AND
       accepted cards, so assigning a still-pending order is two legal moves —
       accepted, then assigned — which submitMiller does in that order rather
       than sending one move the graph refuses with a 409.
     · processing_done → cancelled does NOT exist either. Once an order is
       milled and packed the graph offers only waiting_delivery / ready_pickup,
       so the Done column is not a cancel source: the drop is refused here with
       an explanation instead of becoming a 409 the operator cannot act on.

   Every rule below is enforced CLIENT-SIDE before any request leaves the
   browser, so an impossible drop never becomes an API call. The server
   stays the last word: a refused move answers 409
   { code: "illegal_transition", message } naming both states, and that
   message is surfaced verbatim through react-toastify — never retried.

   MECHANICS: native HTML5 drag and drop only — no new dependency. Touch
   devices (where HTML5 DnD does not exist) get the same moves through a
   long-press / "Move…" sheet listing exactly the legal targets.
═══════════════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ─── Brand palette ─────────────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream:  "#FDF6E3", sand: "#F1E6C9", ink:  "#15281D", stone: "#4C5C46",
  red:    "#C64B3C",
};
const LINE = "var(--mly-border)";

/* ─── Columns, in board order ───────────────────────────────────────── */
export const COLUMNS = [
  { key: "new",       title: "New",              statuses: ["pending", "accepted"],                            accent: BRAND.gold },
  { key: "assigned",  title: "Assigned",         statuses: ["assigned"],                                       accent: BRAND.gold },
  { key: "milling",   title: "Milling/Packing",  statuses: ["preparing"],                                      accent: "#B5781F" },
  { key: "done",      title: "Done",             statuses: ["processing_done", "ready"],                       accent: BRAND.pine },
  { key: "waiting",   title: "Waiting/Ready",    statuses: ["waiting_delivery", "ready_pickup", "out_pickup"], accent: BRAND.pine },
  { key: "outfd",     title: "Out for delivery", statuses: ["out_for_delivery", "out_delivery", "shipped"],    accent: BRAND.forest },
  { key: "delivered", title: "Delivered",        statuses: ["delivered", "left_at_doorstep"],                  accent: BRAND.pine },
  { key: "completed", title: "Completed",        statuses: ["completed"],                                      accent: BRAND.forest },
];

/* slim side column — only rendered when it has cards */
const ATTENTION_STATUSES = ["delivery_failed", "could_not_deliver"];

const COLUMN_STATUSES = COLUMNS.flatMap((c) => c.statuses);
const COLUMN_STATUS_SET = new Set(COLUMN_STATUSES);
const COLUMN_TITLE = COLUMNS.reduce((m, c) => { m[c.key] = c.title; return m; }, { attention: "Needs attention" });
const COLUMN_INDEX = COLUMNS.reduce((m, c, i) => { m[c.key] = i; return m; }, {});

/* what the Dashboard must ask the API for so every column can fill */
export const BOARD_STATUSES = Array.from(new Set([...COLUMN_STATUSES, ...ATTENTION_STATUSES]));
export const BOARD_STATUS_QUERY = BOARD_STATUSES.join(",");

/* ─── Terminal vs active ────────────────────────────────────────────────
   Delivered and Completed are the end of the line: nothing moves out of
   them except delivered → completed, so they are the only two columns
   that get the TODAY-ONLY + 2-hour-bucket treatment. Everything else —
   New … Out for delivery, plus Needs attention — keeps showing every
   card it has, however old, because a stuck order must stay visible.

   The Dashboard imports these two lists so its poller can prioritise
   active rows client-side without re-deriving the split.               */
export const TERMINAL_COLUMN_KEYS = ["delivered", "completed"];
const TERMINAL_KEY_SET = new Set(TERMINAL_COLUMN_KEYS);
export const TERMINAL_BOARD_STATUSES = COLUMNS
  .filter((c) => TERMINAL_KEY_SET.has(c.key))
  .flatMap((c) => c.statuses);
export const ACTIVE_BOARD_STATUSES = BOARD_STATUSES.filter(
  (s) => !TERMINAL_BOARD_STATUSES.includes(s)
);

/* where "+N earlier" sends the store owner — the Orders list has no
   query-param filter on this build, so it is a plain deep link */
const EARLIER_LINK = "/admin/order/recevied";

const RIDER_MSG = "The rider updates this from the MilliyO Rider app";

/* ─── Drag rules ────────────────────────────────────────────────────── */
/* columns whose cards may be picked up at all */
const DRAG_SOURCES = new Set(["new", "assigned", "milling", "done", "waiting", "delivered"]);
/* columns from which the cancel drop-zone is offered.
   "done" (processing_done) is deliberately absent: the graph has no
   processing_done → cancelled edge for any actor, so offering it could only
   ever produce a 409 the operator can do nothing about. */
const CANCEL_SOURCES = new Set(["new", "assigned", "milling"]);
/* columns only the rider can fill */
const RIDER_COLUMNS = new Set(["outfd", "delivered"]);

const isPickupOrder = (o) => o?.fulfilment === "pickup";

/* legal target COLUMNS for a card, mirroring TRANSITIONS + the rider rule */
const legalTargets = (fromKey, isPickup) => {
  switch (fromKey) {
    case "new":       return ["assigned"];                 // POST /assign, then status → assigned
    case "assigned":  return ["milling"];                  // assigned → preparing
    case "milling":   return ["done"];                     // preparing → processing_done
    case "done":      return ["waiting"];                  // → waiting_delivery | ready_pickup
    case "waiting":   return isPickup ? ["completed"] : []; // ready_pickup → completed (handover)
    case "delivered": return ["completed"];                // delivered/left_at_doorstep → completed
    default:          return [];                           // outfd, completed, attention: nothing
  }
};

/* a card is draggable when its column allows it — home-delivery cards
   parked in Waiting/Ready belong to the rider, so they stay put */
const canDragCard = (colKey, order) => {
  if (!DRAG_SOURCES.has(colKey)) return false;
  if (colKey === "waiting") return isPickupOrder(order);
  return true;
};

/* rider-owned cards show the lock + tooltip */
const isRiderLocked = (colKey, order) =>
  colKey === "outfd" || (colKey === "waiting" && !isPickupOrder(order));

/* translate a legal column drop into the concrete server call */
const planMove = (fromKey, toKey, isPickup) => {
  if (!legalTargets(fromKey, isPickup).includes(toKey)) return null;
  switch (toKey) {
    case "assigned":
      return { kind: "miller",  status: "assigned",         stage: "Assigned" };
    case "milling":
      return { kind: "confirm", status: "preparing",        stage: "Milling/Packing" };
    case "done":
      return { kind: "confirm", status: "processing_done",  stage: "Done — milled/packed" };
    case "waiting":
      return isPickup
        ? { kind: "confirm", status: "ready_pickup",     stage: "Ready for pickup" }
        : { kind: "rider",   status: "waiting_delivery", stage: "Waiting for delivery" };
    case "completed":
      return { kind: "confirm", status: "completed", stage: "Completed" };
    default:
      return null;
  }
};

/* ─── Small helpers ─────────────────────────────────────────────────── */
const MAX_CARDS = 12;        // cap for the ordinary (active) columns
const MAX_PER_BUCKET = 8;    // cap INSIDE each 2-hour bucket

/* ═══════════════════════════════════════════════════════════════════════
   IST DAY + 2-HOUR BUCKETS

   The store runs on Asia/Kolkata and the browser may not. India has had
   no DST since 1945, so IST is a fixed +05:30 — shifting the instant by
   that offset and then reading the *UTC* getters yields IST wall-clock
   exactly, with no date library and no trust placed in the machine's
   local zone. (Intl.DateTimeFormat would also work but allocates a
   formatter per card on every 10s repaint.)
═══════════════════════════════════════════════════════════════════════ */
const IST_OFFSET_MS  = (5 * 60 + 30) * 60 * 1000;
const BUCKET_HOURS   = 2;
const BUCKET_COUNT   = 24 / BUCKET_HOURS;      // 12 slots: 00–02 … 22–24

const istShifted = (ms) => new Date(ms + IST_OFFSET_MS);

/* YYYYMMDD in IST — cheap to compare, safe across month/year rollover */
const istDayKey = (ms) => {
  const d = istShifted(ms);
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
};

const istBucketIndex = (ms) =>
  Math.min(BUCKET_COUNT - 1, Math.max(0, Math.floor(istShifted(ms).getUTCHours() / BUCKET_HOURS)));

/* 12-hour labels: "2:00 – 4:00 pm", and "10:00 am – 12:00 pm" when the
   slot straddles noon/midnight */
const hour12   = (h) => { const v = (((h % 24) + 24) % 24) % 12; return v === 0 ? 12 : v; };
const meridiem = (h) => ((((h % 24) + 24) % 24) < 12 ? "am" : "pm");

const bucketLabel = (i) => {
  const from = i * BUCKET_HOURS;
  const to   = from + BUCKET_HOURS;
  return meridiem(from) === meridiem(to)
    ? `${hour12(from)}:00 – ${hour12(to)}:00 ${meridiem(to)}`
    : `${hour12(from)}:00 ${meridiem(from)} – ${hour12(to)}:00 ${meridiem(to)}`;
};

/* the timestamp a terminal card is filed under: when it last moved.
   NOTE: `updatedAt` is bumped by ANY write (a payment sync, a bulk
   migration…), so an old completed order whose row was touched today would
   wrongly resurface in "Completed today". The reliable signal is the
   status-history entry that actually put the order into this terminal column
   — but GET /admin/orders does not carry one (only the detail route answers
   `history`), so on the board this falls back to updatedAt/placedAt. */
const stampMs = (o) => {
  const raw = o?.updatedAt || o?.placedAt;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : NaN;
};

/* status -> terminal column key, so a history entry can be matched to a column */
const TERMINAL_STATUS_BY_KEY = COLUMNS.filter((c) =>
  TERMINAL_COLUMN_KEYS.includes(c.key)
).reduce((m, c) => {
  m[c.key] = new Set(c.statuses);
  return m;
}, {});

/* When this terminal order actually reached this column (IST-agnostic epoch). */
const terminalStampMs = (o, key) => {
  const wanted = TERMINAL_STATUS_BY_KEY[key];
  const hist = Array.isArray(o?.history) ? o.history : [];
  // newest history entry whose status belongs to THIS column
  if (wanted) {
    for (let i = hist.length - 1; i >= 0; i -= 1) {
      if (wanted.has(hist[i]?.status)) {
        const t = new Date(hist[i].at).getTime();
        if (Number.isFinite(t)) return t;
      }
    }
  }
  // else the most recent real status transition of any kind
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const t = hist[i]?.at ? new Date(hist[i].at).getTime() : NaN;
    if (Number.isFinite(t)) return t;
  }
  // no usable history — fall back to the (bump-prone) doc timestamps
  return stampMs(o);
};

const minsAgo = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d`;
};

/* An order is addressed by orderNo everywhere now — there is no _id to shorten.
   The tail is enough to tell two of a day's orders apart on a card. */
const shortId = (o) => `#${String(o?.orderNo || "").slice(-6)}`;

/* Every amount on /admin/orders is an integer number of PAISE. */
const rupees = (paise) =>
  ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/* Errors are { code, message } with a real HTTP status — the 409 from a
   refused move carries the transition graph's own wording. */
const serverMsg = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.code || err?.message || fallback;

/* GET /admin/staff is one of the payloads whose own top-level key is "data". */
const staffListOf = (res) => {
  const list = res?.data?.data;
  return (Array.isArray(list) ? list : []).filter((s) => s && s.isActive !== false);
};

/* ═══════════════════════════════════════════════════════════════════════
   ORDER CARD
═══════════════════════════════════════════════════════════════════════ */
const OrderCard = ({
  order, draggable, locked, dragging, moveCount,
  onDragStart, onDragEnd, onOpen, onSheet,
}) => {
  const pressTimer  = useRef(null);
  const longFired   = useRef(false);
  const pickup      = isPickupOrder(order);
  /* The staff NAME is copied onto the order, not a reference to a staff row —
     the order still reads correctly after the staff member leaves. */
  const miller      = order?.millerName || "";
  const rider       = order?.deliveryName || "";

  const clearPress = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };

  /* touch fallback — HTML5 DnD does not exist on touch, so a long press
     opens the very same "Move to…" sheet with the same legal targets */
  const startPress = () => {
    if (!moveCount) return;
    longFired.current = false;
    clearPress();
    pressTimer.current = setTimeout(() => {
      longFired.current = true;
      onSheet();
    }, 480);
  };

  const handleClick = () => {
    if (longFired.current) { longFired.current = false; return; }
    onOpen();
  };

  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      onClick={handleClick}
      onTouchStart={startPress}
      onTouchMove={clearPress}
      onTouchEnd={clearPress}
      onTouchCancel={clearPress}
      title={
        locked
          ? RIDER_MSG
          : draggable
          ? "Drag to the next stage — or use Move…"
          : "Open order"
      }
      style={{
        background: "var(--mly-surface)",
        border: `1px solid ${dragging ? BRAND.gold : LINE}`,
        borderRadius: 10,
        padding: "8px 10px",
        marginBottom: 6,
        cursor: draggable ? "grab" : "pointer",
        opacity: dragging ? 0.42 : 1,
        boxShadow: dragging ? `0 6px 16px rgba(14,58,44,0.18)` : "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "manipulation",
        transition: "border-color .12s, opacity .12s",
      }}
      onMouseEnter={(e) => { if (!dragging) e.currentTarget.style.borderColor = BRAND.gold; }}
      onMouseLeave={(e) => { if (!dragging) e.currentTarget.style.borderColor = LINE; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mly-heading)", display: "flex", alignItems: "center", gap: 4 }}>
          {draggable && (
            <span aria-hidden="true" style={{ color: "var(--mly-text-muted)", fontSize: 11, letterSpacing: -1 }}>⠿</span>
          )}
          {shortId(order)}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {locked && (
            <span title={RIDER_MSG} aria-label={RIDER_MSG} style={{ lineHeight: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--mly-text-soft)"
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
          )}
          <span style={{ fontSize: 10.5, color: "var(--mly-text-muted)", fontWeight: 600 }}>
            {order.placedAt ? minsAgo(order.placedAt) : ""}
          </span>
        </span>
      </div>

      <div style={{ fontSize: 11, color: "var(--mly-text-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {order.shipName || order.customerName || "—"}
        {order.splitCount > 1 ? ` · split ${order.splitIndex}/${order.splitCount}` : ""}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2, alignItems: "center" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-heading)" }}>₹{rupees(order.totalPaise)}</span>
        <span style={{ fontSize: 10, color: pickup ? "var(--mly-accent)" : "var(--mly-text-soft)", fontWeight: 700 }}>
          {pickup ? "PICKUP" : "DELIVERY"}
        </span>
      </div>

      {(miller || rider || order.retryRequested) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
          {miller && (
            <span style={{
              background: BRAND.cream, color: BRAND.gold, border: `1px solid ${BRAND.honey}`,
              borderRadius: 999, fontSize: 9.5, fontWeight: 700, padding: "1px 7px",
            }}>M · {miller}</span>
          )}
          {rider && (
            <span style={{
              background: "#E4F0EA", color: BRAND.pine, border: "1px solid #9CC7B4",
              borderRadius: 999, fontSize: 9.5, fontWeight: 700, padding: "1px 7px",
            }}>R · {rider}</span>
          )}
          {order.retryRequested && (
            <span style={{
              background: "#FDECEA", color: BRAND.red, border: `1px solid ${BRAND.red}`,
              borderRadius: 999, fontSize: 9.5, fontWeight: 700, padding: "1px 7px",
            }}>RETRY ASKED</span>
          )}
        </div>
      )}

      {moveCount > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSheet(); }}
          style={{
            marginTop: 6, width: "100%", border: `1px dashed ${BRAND.honey}`,
            background: BRAND.cream, color: BRAND.gold, borderRadius: 8,
            fontSize: 10, fontWeight: 800, letterSpacing: 0.4, padding: "3px 0",
            cursor: "pointer",
          }}
        >
          Move to…
        </button>
      )}

      {locked && (
        <div style={{ fontSize: 9.5, color: "var(--mly-text-soft)", marginTop: 5, fontStyle: "italic" }}>
          {RIDER_MSG}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   MODAL SHELL — brand-styled, click-outside + Escape dismissable
═══════════════════════════════════════════════════════════════════════ */
const ModalShell = ({ title, eyebrow, tone = "gold", onClose, children, footer }) => {
  const accent = tone === "danger" ? BRAND.red : tone === "pine" ? BRAND.pine : BRAND.gold;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(21,40,29,0.45)",
          backdropFilter: "blur(2px)", zIndex: 1040,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          zIndex: 1050, width: "calc(100% - 32px)", maxWidth: 430,
          background: "var(--mly-surface)", borderRadius: 20,
          boxShadow: "0 20px 60px rgba(14,58,44,0.22)",
          borderTop: `4px solid ${accent}`,
          padding: "22px 24px 20px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            {eyebrow && (
              <div style={{
                fontSize: 10, fontWeight: 800, color: accent,
                textTransform: "uppercase", letterSpacing: 1.4, marginBottom: 3,
              }}>{eyebrow}</div>
            )}
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--mly-heading)" }}>{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: "50%", border: `1px solid ${LINE}`,
              background: "var(--mly-surface-2)", cursor: "pointer", fontSize: 15, color: "var(--mly-text-soft)",
              lineHeight: 1, flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <hr style={{ borderColor: "var(--mly-border-soft)", margin: "14px 0 14px" }} />

        {children}

        {footer && <div style={{ display: "flex", gap: 10, marginTop: 18 }}>{footer}</div>}

        <div style={{ fontSize: 10, color: "var(--mly-text-muted)", marginTop: 10, textAlign: "center" }}>
          Press Esc to dismiss · live refresh is paused
        </div>
      </div>
    </>
  );
};

const btnGhost = {
  padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${LINE}`,
  background: "var(--mly-surface-2)", color: "var(--mly-text-soft)", fontWeight: 700, fontSize: 13,
  cursor: "pointer",
};
const btnPrimary = (disabled, tone = BRAND.pine) => ({
  flex: 1, padding: "10px 18px", borderRadius: 10, border: "none",
  background: disabled ? "#B9C9C0" : tone, color: "#fff",
  fontWeight: 800, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer",
});
const selectStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10,
  border: `1.5px solid ${LINE}`, fontSize: 13, outline: "none",
  color: "var(--mly-text)", background: "var(--mly-surface)", cursor: "pointer", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: 10, fontWeight: 800, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 6, textTransform: "uppercase",
};

/* ═══════════════════════════════════════════════════════════════════════
   BOARD
═══════════════════════════════════════════════════════════════════════ */
const PipelineBoard = ({ orders = [], onRefresh, onBusyChange }) => {
  const navigate = useNavigate();

  const [drag,       setDrag]       = useState(null);   // { id, from, isPickup }
  const [overCol,    setOverCol]    = useState(null);
  const [overCancel, setOverCancel] = useState(false);
  const [modal,      setModal]      = useState(null);   // { order, from, to, plan, kind }
  const [sheet,      setSheet]      = useState(null);   // { order, from }
  const [optimistic, setOptimistic] = useState({});     // id → status shown before the server agrees
  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState("");
  const [selStaff,   setSelStaff]   = useState("");
  const [note,       setNote]       = useState("");
  const [staff,      setStaff]      = useState({ miller_packer: [], delivery: [] });
  const [staffBusy,  setStaffBusy]  = useState(false);
  /* bucketKey → true|false, holding ONLY the buckets the user has clicked.
     Anything absent falls back to the default (open iff it is the slot the
     clock is in right now), so the 10s poller can never collapse a group
     the user opened — this map is plain component state and outlives every
     refresh for as long as the page is mounted. */
  const [openBuckets, setOpenBuckets] = useState({});

  /* ── pause the Dashboard's 10s poller while a drag or a modal is live,
        so cards never jump under the cursor ── */
  const busy = !!drag || !!modal || !!sheet;
  useEffect(() => {
    if (onBusyChange) onBusyChange(busy);
  }, [busy, onBusyChange]);
  useEffect(() => () => { if (onBusyChange) onBusyChange(false); }, [onBusyChange]);

  /* ── Escape closes the modal / sheet ── */
  useEffect(() => {
    if (!modal && !sheet) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || saving) return;
      setModal(null);
      setSheet(null);
      setModalError("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, sheet, saving]);

  /* ── drop optimistic entries the server has caught up with (or that no
        longer exist on the board at all — a cancelled order leaves it) ── */
  useEffect(() => {
    setOptimistic((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const serverStatus = new Map((orders || []).filter(Boolean).map((o) => [o.orderNo, o.status]));
      const next = {};
      let changed = false;
      keys.forEach((id) => {
        const real = serverStatus.get(id);
        if (real === undefined || real === prev[id]) { changed = true; return; }
        next[id] = prev[id];
      });
      return changed ? next : prev;
    });
  }, [orders]);

  /* ── group orders into columns (optimistic status wins) ── */
  const grouped = useMemo(() => {
    const map = { attention: [] };
    COLUMNS.forEach((c) => { map[c.key] = []; });
    (orders || []).forEach((o) => {
      if (!o || !o.orderNo) return;
      const status = optimistic[o.orderNo] || o.status;
      if (ATTENTION_STATUSES.includes(status) || (o.retryRequested && !COLUMN_STATUS_SET.has(status))) {
        map.attention.push(o);
        return;
      }
      const col = COLUMNS.find((c) => c.statuses.includes(status));
      if (col) map[col.key].push(o);
    });
    return map;
  }, [orders, optimistic]);

  /* ── terminal columns: today only, sliced into IST 2-hour buckets ──
        Recomputed off `grouped`, so it refreshes on every poll — which is
        also how the "current" bucket rolls forward without its own timer.
        A card being moved optimistically is filed under *now*, so it lands
        in the live bucket instead of vanishing into a collapsed earlier
        one while the server catches up. */
  const terminalView = useMemo(() => {
    const nowMs   = Date.now();
    const today   = istDayKey(nowMs);
    const nowSlot = istBucketIndex(nowMs);
    const out = {};

    TERMINAL_COLUMN_KEYS.forEach((key) => {
      const cards   = grouped[key] || [];
      const buckets = Array.from({ length: BUCKET_COUNT }, () => []);
      let earlier   = 0;
      let todayCount = 0;

      cards.forEach((o) => {
        const ms = optimistic[o.orderNo] ? nowMs : terminalStampMs(o, key);
        /* a row with no usable timestamp is never silently dropped — it
           rides in the live bucket where it stays in the owner's face */
        if (!Number.isFinite(ms)) {
          buckets[nowSlot].push({ o, ms: nowMs });
          todayCount += 1;
          return;
        }
        if (istDayKey(ms) !== today) { earlier += 1; return; }
        buckets[istBucketIndex(ms)].push({ o, ms });
        todayCount += 1;
      });

      /* newest slot first, newest card first inside it */
      const groups = [];
      for (let i = BUCKET_COUNT - 1; i >= 0; i -= 1) {
        if (!buckets[i].length) continue;
        buckets[i].sort((a, b) => b.ms - a.ms);
        groups.push({
          index: i,
          label: bucketLabel(i),
          isCurrent: i === nowSlot,
          cards: buckets[i].map((x) => x.o),
        });
      }
      out[key] = { groups, todayCount, earlier, dayKey: today };
    });

    return out;
  }, [grouped, optimistic]);

  /* default: closed, except the slot the clock is in — a user click on a
     bucket wins from then on (the day key is part of the id, so a rollover
     past midnight IST hands the defaults back) */
  const bucketOpen = (bucketKey, isCurrent) =>
    Object.prototype.hasOwnProperty.call(openBuckets, bucketKey)
      ? openBuckets[bucketKey]
      : isCurrent;

  const toggleBucket = (bucketKey, isCurrent) =>
    setOpenBuckets((prev) => ({
      ...prev,
      [bucketKey]: !(Object.prototype.hasOwnProperty.call(prev, bucketKey) ? prev[bucketKey] : isCurrent),
    }));

  const findOrder = useCallback(
    (orderNo) => (orders || []).find((o) => o && o.orderNo === orderNo),
    [orders]
  );

  /* ── staff lists (loaded lazily, active only) ──
     Kept once loaded. Assigning is the most repeated action on this board, and
     fetching the same six names on every single open put "Loading staff…" in
     front of the owner each time. A list that has been fetched is refreshed in
     the background instead, so a newly hired rider still turns up without the
     wait. */
  const loadStaff = useCallback(async (role) => {
    let known = false;
    setStaff((prev) => { known = Array.isArray(prev[role]) && prev[role].length > 0; return prev; });
    if (!known) setStaffBusy(true);
    try {
      const res = await api.get(`/admin/staff?role=${role}`);
      setStaff((prev) => ({ ...prev, [role]: staffListOf(res) }));
    } catch (err) {
      // A refresh that fails behind a list already on screen is not worth an
      // error box: the names shown are the ones we had a moment ago.
      if (!known) setModalError(serverMsg(err, "Failed to load the staff list"));
    } finally {
      setStaffBusy(false);
    }
  }, []);

  /* Runs the real calls behind a move. The card jumps first (optimistic);
     a failure puts it back and shows exactly what the server said (409
     illegal_transition and the 403 wrong_store refusal included). */
  const commit = useCallback(async ({ order, status, run, success }) => {
    setSaving(true);
    setModalError("");
    /* optimistic move */
    setOptimistic((prev) => ({ ...prev, [order.orderNo]: status }));
    try {
      await run();
      toast.success(success);
      setModal(null);
      setSheet(null);
      if (onRefresh) { try { await onRefresh(); } catch (e) { /* poller will catch up */ } }
    } catch (err) {
      /* rollback */
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[order.orderNo];
        return next;
      });
      const msg = serverMsg(err, "Could not update this order");
      setModalError(msg);
      toast.error(`${shortId(order)} · ${msg}`, { autoClose: 7000 });
      /* re-sync with the truth — a 409 usually means our view was stale */
      if (onRefresh) { try { await onRefresh(); } catch (e) { /* ignore */ } }
    } finally {
      setSaving(false);
    }
  }, [onRefresh]);

  /* ── open the right prompt for a legal move ── */
  const openMove = useCallback((order, fromKey, toKey, plan) => {
    setModalError("");
    setSelStaff("");
    setNote("");
    setSheet(null);
    setModal({ order, from: fromKey, to: toKey, plan, kind: plan.kind });
    if (plan.kind === "miller") loadStaff("miller_packer");
    if (plan.kind === "rider")  loadStaff("delivery");
  }, [loadStaff]);

  const openCancel = useCallback((order, fromKey) => {
    setModalError("");
    setSheet(null);
    setModal({ order, from: fromKey, to: "cancelled", kind: "cancel", plan: { kind: "cancel", status: "cancelled", stage: "Cancelled" } });
  }, []);

  /* ── refused drops: explain, never call the API ── */
  const refuse = useCallback((order, fromKey, toKey) => {
    const from = COLUMN_TITLE[fromKey] || fromKey;
    const to   = COLUMN_TITLE[toKey]   || toKey;
    const fi   = COLUMN_INDEX[fromKey];
    const ti   = COLUMN_INDEX[toKey];
    let why;
    if (RIDER_COLUMNS.has(toKey) && !isPickupOrder(order)) {
      why = `${to} is the rider's leg. ${RIDER_MSG}.`;
    } else if (Number.isInteger(fi) && Number.isInteger(ti) && ti < fi) {
      why = `the pipeline only moves forward — ${from} → ${to} is backwards. Cancel the order instead.`;
    } else if (toKey === "attention") {
      why = "Needs attention is filled by failed delivery attempts, not by hand.";
    } else {
      why = `${from} → ${to} is not a step this order can take.`;
    }
    toast.warn(`Not allowed — ${why}`, { autoClose: 5000 });
  }, []);

  /* ── HTML5 drag plumbing ── */
  const handleDragStart = (order, fromKey) => (e) => {
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", order.orderNo);
    } catch (err) { /* older browsers */ }
    setDrag({ id: order.orderNo, from: fromKey, isPickup: isPickupOrder(order) });
  };

  const handleDragEnd = () => {
    setDrag(null);
    setOverCol(null);
    setOverCancel(false);
  };

  const dragLegalTargets = drag
    ? legalTargets(drag.from, drag.isPickup)
    : [];

  const columnState = (colKey) => {
    if (!drag) return "idle";
    if (drag.from === colKey) return "source";
    return dragLegalTargets.includes(colKey) ? "legal" : "illegal";
  };

  const handleDragOver = (colKey) => (e) => {
    /* preventDefault on every column so the drop event always fires — the
       legality check below runs before anything is sent anywhere, and an
       illegal drop gets an explanation instead of silence */
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    setOverCol((cur) => (cur === colKey ? cur : colKey));
  };

  const handleDragLeave = (colKey) => (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setOverCol((cur) => (cur === colKey ? null : cur));
  };

  const handleDrop = (colKey) => (e) => {
    e.preventDefault();
    setOverCol(null);
    setOverCancel(false);
    const current = drag;
    setDrag(null);
    if (!current) return;
    const order = findOrder(current.id);
    if (!order) return;
    if (current.from === colKey) return;                  // dropped home — no-op
    const plan = planMove(current.from, colKey, isPickupOrder(order));
    if (!plan) { refuse(order, current.from, colKey); return; }
    openMove(order, current.from, colKey, plan);
  };

  const handleCancelDrop = (e) => {
    e.preventDefault();
    setOverCancel(false);
    setOverCol(null);
    const current = drag;
    setDrag(null);
    if (!current) return;
    const order = findOrder(current.id);
    if (!order) return;
    if (!CANCEL_SOURCES.has(current.from)) {
      refuse(order, current.from, "cancelled");
      return;
    }
    openCancel(order, current.from);
  };

  /* ── touch sheet ── */
  const openSheet = (order, fromKey) => {
    setSheet({ order, from: fromKey });
    setModal(null);
    setModalError("");
  };

  const sheetOptions = (order, fromKey) => {
    const targets = legalTargets(fromKey, isPickupOrder(order));
    const opts = targets
      .map((toKey) => {
        const plan = planMove(fromKey, toKey, isPickupOrder(order));
        return plan ? { toKey, plan, label: plan.stage } : null;
      })
      .filter(Boolean);
    if (CANCEL_SOURCES.has(fromKey)) opts.push({ toKey: "cancelled", plan: null, label: "Cancel order" });
    return opts;
  };

  const moveCountFor = (order, colKey) => {
    if (!canDragCard(colKey, order)) return 0;
    return sheetOptions(order, colKey).length;
  };

  /* ── the actual calls, one per prompt ──
     Every write is a POST to /fulfilment addressed by orderNo. The staff id is
     a number (staffId), and assigning no longer moves the order: POST /assign
     only copies the chosen staff member's NAME onto it, so the status change
     that used to ride along with the old auto-advancing PATCH is now its own
     call. */
  const submitMiller = () => {
    if (!modal || !selStaff) return;
    const { order } = modal;
    const chosen = staff.miller_packer.find((s) => String(s.id) === selStaff);
    commit({
      order,
      status: "assigned",
      run: async () => {
        await api.post(`/fulfilment/orders/${order.orderNo}/assign`, { staffId: Number(selStaff) });
        /* The New column holds pending AND accepted cards, and the graph has
           no pending → assigned edge — accepting first is the other half of
           the same move, not an extra one. Sending assigned straight from
           pending is a 409 the operator cannot clear from this board. */
        if (order.status === "pending") {
          await api.post(`/fulfilment/orders/${order.orderNo}/status`, { status: "accepted" });
        }
        await api.post(`/fulfilment/orders/${order.orderNo}/status`, {
          status: "assigned",
          ...(note.trim() ? { note: note.trim() } : {}),
        });
      },
      success: `${shortId(order)} assigned to ${chosen?.name || "the miller/packer"}`,
    });
  };

  const submitRider = () => {
    if (!modal || !selStaff) return;
    const { order } = modal;
    const chosen = staff.delivery.find((s) => String(s.id) === selStaff);
    commit({
      order,
      status: "waiting_delivery",
      run: async () => {
        await api.post(`/fulfilment/orders/${order.orderNo}/assign`, { staffId: Number(selStaff) });
        await api.post(`/fulfilment/orders/${order.orderNo}/status`, { status: "waiting_delivery" });
      },
      success: `${shortId(order)} is waiting for ${chosen?.name || "the rider"} to pick it up`,
    });
  };

  const submitConfirm = () => {
    if (!modal) return;
    const { order, plan } = modal;
    commit({
      order,
      status: plan.status,
      run: () => api.post(`/fulfilment/orders/${order.orderNo}/status`, { status: plan.status }),
      success: `${shortId(order)} moved to ${plan.stage}`,
    });
  };

  const submitCancel = () => {
    if (!modal) return;
    const { order } = modal;
    commit({
      order,
      status: "cancelled",
      run: () => api.post(`/fulfilment/orders/${order.orderNo}/status`, { status: "cancelled" }),
      success: `${shortId(order)} cancelled — stock and milling slots released`,
    });
  };

  /* ── rendering ── */
  const attention = grouped.attention || [];
  const showAttention = attention.length > 0;
  const showCancelZone = !!drag && CANCEL_SOURCES.has(drag.from);

  /* one card, identical wherever it is rendered (flat column or bucket) */
  const renderCard = (o, colKey, slim) => (
    <OrderCard
      key={o.orderNo}
      order={o}
      draggable={!slim && canDragCard(colKey, o)}
      locked={!slim && isRiderLocked(colKey, o)}
      dragging={drag?.id === o.orderNo}
      moveCount={slim ? 0 : moveCountFor(o, colKey)}
      onDragStart={handleDragStart(o, colKey)}
      onDragEnd={handleDragEnd}
      onOpen={() => navigate(`/admin/orders/details/${o.orderNo}`)}
      onSheet={() => openSheet(o, colKey)}
    />
  );

  const moreLine = (n) => (
    <div style={{ fontSize: 10, color: "var(--mly-text-muted)", textAlign: "center", fontWeight: 700 }}>
      +{n} more
    </div>
  );

  const renderColumn = (col, cards, slim = false) => {
    const state = slim ? "illegal" : columnState(col.key);
    const isLegal = state === "legal";
    const isHot   = isLegal && overCol === col.key;
    const shown   = cards.slice(0, MAX_CARDS);
    /* Delivered + Completed render today's cards only, in 2-hour groups */
    const terminal = !slim ? terminalView[col.key] : undefined;
    const headCount = terminal ? terminal.todayCount : cards.length;

    return (
      <div
        key={col.key}
        onDragEnter={handleDragOver(col.key)}
        onDragOver={handleDragOver(col.key)}
        onDragLeave={handleDragLeave(col.key)}
        onDrop={handleDrop(col.key)}
        style={{
          background: isHot ? "var(--mly-accent-soft)" : isLegal ? "var(--mly-surface-3)" : "var(--mly-surface-2)",
          border: isLegal ? `2px dashed ${BRAND.gold}` : `1px solid ${LINE}`,
          borderRadius: 14,
          padding: isLegal ? 9 : 10,
          minHeight: 92,
          opacity: drag && state === "illegal" ? 0.45 : 1,
          transition: "opacity .12s, background .12s, border-color .12s",
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${col.accent}`,
          gap: 6,
        }}>
          <span style={{
            fontSize: slim ? 11 : 12, fontWeight: 700, color: "var(--mly-heading)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            display: "flex", alignItems: "baseline", gap: 5, minWidth: 0,
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{col.title}</span>
            {terminal && (
              <span style={{
                flexShrink: 0, background: "var(--mly-surface-3)", color: "var(--mly-text-soft)",
                borderRadius: 999, fontSize: 8.5, fontWeight: 800,
                letterSpacing: 0.8, textTransform: "uppercase", padding: "1px 6px",
              }}>
                Today
              </span>
            )}
          </span>
          <span style={{
            background: col.accent, color: "#fff", borderRadius: 999,
            fontSize: 11, fontWeight: 700, padding: "1px 9px", flexShrink: 0,
          }}>
            {headCount}
          </span>
        </div>

        {terminal ? (
          <>
            {terminal.groups.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--mly-text-muted)", textAlign: "center", padding: "10px 0" }}>
                {isLegal ? "Drop here" : "—"}
              </div>
            ) : (
              terminal.groups.map((g) => {
                const bucketKey = `${col.key}|${terminal.dayKey}|${g.index}`;
                const open      = bucketOpen(bucketKey, g.isCurrent);
                const inBucket  = g.cards.slice(0, MAX_PER_BUCKET);
                return (
                  <div key={bucketKey} style={{ marginBottom: 2 }}>
                    {/* quiet section divider, not a card */}
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggleBucket(bucketKey, g.isCurrent)}
                      title={open ? "Hide this 2-hour slot" : "Show this 2-hour slot"}
                      style={{
                        width: "100%", display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: 6,
                        background: "var(--mly-surface-3)", color: "var(--mly-text)",
                        border: "none", borderRadius: 7, textAlign: "left",
                        padding: "3px 7px", marginBottom: 5, cursor: "pointer",
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.1,
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        <svg
                          width="8" height="8" viewBox="0 0 24 24" fill="none"
                          stroke="var(--mly-text-soft)" strokeWidth="4" strokeLinecap="round"
                          strokeLinejoin="round" aria-hidden="true"
                          style={{
                            flexShrink: 0,
                            transform: open ? "rotate(90deg)" : "none",
                            transition: "transform .12s",
                          }}
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {g.label}
                        </span>
                      </span>
                      <span style={{ color: "var(--mly-text-soft)", fontWeight: 800, fontSize: 10, flexShrink: 0 }}>
                        {g.cards.length}
                      </span>
                    </button>

                    {open && (
                      <>
                        {inBucket.map((o) => renderCard(o, col.key, slim))}
                        {g.cards.length > inBucket.length && moreLine(g.cards.length - inBucket.length)}
                      </>
                    )}
                  </div>
                );
              })
            )}

            {terminal.earlier > 0 && (
              <button
                type="button"
                onClick={() => navigate(EARLIER_LINK)}
                title="Older orders live in the Orders list"
                style={{
                  width: "100%", marginTop: 4, padding: "4px 0",
                  background: "transparent", border: "none",
                  borderTop: `1px dashed ${LINE}`,
                  color: "var(--mly-text-muted)", fontSize: 10, fontWeight: 600,
                  textAlign: "center", cursor: "pointer",
                }}
              >
                +{terminal.earlier} earlier — view in Orders
              </button>
            )}
          </>
        ) : (
          <>
            {shown.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--mly-text-muted)", textAlign: "center", padding: "10px 0" }}>
                {isLegal ? "Drop here" : "—"}
              </div>
            ) : (
              shown.map((o) => renderCard(o, col.key, slim))
            )}

            {cards.length > shown.length && moreLine(cards.length - shown.length)}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* ── eyebrow ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 8, marginBottom: 6, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 10.5, fontWeight: 700, color: "var(--mly-accent-2)",
          textTransform: "uppercase", letterSpacing: "1.5px",
        }}>
          Live Pipeline · drag a card to its next stage
        </div>
        <div style={{ fontSize: 10, color: busy ? BRAND.red : "var(--mly-text-muted)", fontWeight: 700 }}>
          {busy ? "● auto-refresh paused" : "● auto-updating every 10s"}
        </div>
      </div>

      {/* ── the board ── */}
      <div style={{ overflowX: "auto", paddingBottom: 4 }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: showAttention
            ? `repeat(${COLUMNS.length}, minmax(186px, 1fr)) minmax(140px, 0.7fr)`
            : `repeat(${COLUMNS.length}, minmax(186px, 1fr))`,
          gap: 10,
          alignItems: "start",
        }}>
          {COLUMNS.map((col) => renderColumn(col, grouped[col.key] || []))}

          {showAttention &&
            renderColumn(
              { key: "attention", title: "Needs attention", accent: BRAND.red, statuses: ATTENTION_STATUSES },
              attention,
              true
            )}
        </div>
      </div>

      {/* ── cancel drop-zone — only while dragging a cancellable card ── */}
      {showCancelZone && (
        <div
          onDragEnter={(e) => { e.preventDefault(); setOverCancel(true); }}
          onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; setOverCancel(true); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOverCancel(false); }}
          onDrop={handleCancelDrop}
          style={{
            marginTop: 10, borderRadius: 14,
            border: `2px dashed ${BRAND.red}`,
            background: overCancel ? "#F8D7D2" : "#FDECEA",
            color: BRAND.red, textAlign: "center",
            padding: overCancel ? "18px 12px" : "14px 12px",
            fontSize: 12.5, fontWeight: 800, letterSpacing: 0.3,
            transition: "padding .12s, background .12s",
          }}
        >
          Drop here to cancel this order
          <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 2, color: "#8E3A2F" }}>
            Held stock and milling-slot capacity are released
          </div>
        </div>
      )}

      {/* ═══ MOVE-TO SHEET (touch fallback / explicit menu) ═══ */}
      {sheet && (
        <ModalShell
          eyebrow="Move to"
          title={`${shortId(sheet.order)} · ${COLUMN_TITLE[sheet.from] || sheet.from}`}
          onClose={() => setSheet(null)}
        >
          <div style={{ fontSize: 12, color: "var(--mly-text-soft)", marginBottom: 12 }}>
            {isPickupOrder(sheet.order) ? "Store pickup" : "Home delivery"} ·{" "}
            {sheet.order.shipName || sheet.order.customerName || "—"}
          </div>
          {sheetOptions(sheet.order, sheet.from).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--mly-text-soft)" }}>
              No portal move is possible from here. {RIDER_MSG}.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sheetOptions(sheet.order, sheet.from).map((opt) => (
                <button
                  key={opt.toKey}
                  type="button"
                  onClick={() =>
                    opt.plan
                      ? openMove(sheet.order, sheet.from, opt.toKey, opt.plan)
                      : openCancel(sheet.order, sheet.from)
                  }
                  style={{
                    width: "100%", padding: "11px 14px", borderRadius: 10, textAlign: "left",
                    border: opt.plan ? "1.5px solid transparent" : `1.5px solid ${BRAND.red}`,
                    background: opt.plan ? BRAND.gold : "#FDECEA",
                    color: opt.plan ? "#fff" : BRAND.red,
                    fontWeight: 800, fontSize: 13, cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </ModalShell>
      )}

      {/* ═══ ASSIGN MILLER/PACKER ═══ */}
      {modal?.kind === "miller" && (
        <ModalShell
          eyebrow={`${COLUMN_TITLE[modal.from]} → Assigned`}
          title="Assign miller/packer"
          onClose={() => !saving && setModal(null)}
          footer={
            <>
              <button type="button" style={btnGhost} onClick={() => !saving && setModal(null)}>Cancel</button>
              <button
                type="button"
                onClick={submitMiller}
                disabled={saving || !selStaff}
                style={btnPrimary(saving || !selStaff)}
              >
                {saving ? "Assigning…" : "Assign"}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 12, color: "var(--mly-text-soft)", marginBottom: 14 }}>
            Order <strong style={{ color: "var(--mly-heading)" }}>{shortId(modal.order)}</strong> ·{" "}
            {modal.order.shipName || modal.order.customerName || "—"} · ₹{rupees(modal.order.totalPaise)}
          </div>

          {modalError && (
            <div style={{ background: "#FDECEA", color: "#C62828", padding: "9px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
              {modalError}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle} htmlFor="pb-miller">Miller / Packer</label>
            <select
              id="pb-miller"
              autoFocus
              value={selStaff}
              onChange={(e) => setSelStaff(e.target.value)}
              style={selectStyle}
            >
              <option value="">{staffBusy ? "Loading staff…" : "— Choose staff —"}</option>
              {staff.miller_packer.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {!staffBusy && staff.miller_packer.length === 0 && (
              <div style={{ fontSize: 11, color: BRAND.red, marginTop: 5, fontWeight: 600 }}>
                No active miller/packer found — add one under Staff first.
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="pb-note">Note (optional)</label>
            <input
              id="pb-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. mill the ragi first"
              style={{ ...selectStyle, cursor: "text" }}
            />
          </div>
        </ModalShell>
      )}

      {/* ═══ ASSIGN DELIVERY RIDER ═══ */}
      {modal?.kind === "rider" && (
        <ModalShell
          eyebrow={`${COLUMN_TITLE[modal.from]} → Waiting for delivery`}
          title="Assign delivery rider"
          tone="pine"
          onClose={() => !saving && setModal(null)}
          footer={
            <>
              <button type="button" style={btnGhost} onClick={() => !saving && setModal(null)}>Cancel</button>
              <button
                type="button"
                onClick={submitRider}
                disabled={saving || !selStaff}
                style={btnPrimary(saving || !selStaff)}
              >
                {saving ? "Saving…" : "Assign & set waiting"}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 12, color: "var(--mly-text-soft)", marginBottom: 14 }}>
            Order <strong style={{ color: "var(--mly-heading)" }}>{shortId(modal.order)}</strong> ·{" "}
            {modal.order.shipName || modal.order.customerName || "—"} · home delivery
          </div>

          {modalError && (
            <div style={{ background: "#FDECEA", color: "#C62828", padding: "9px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
              {modalError}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle} htmlFor="pb-rider">Delivery rider</label>
            <select
              id="pb-rider"
              autoFocus
              value={selStaff}
              onChange={(e) => setSelStaff(e.target.value)}
              style={selectStyle}
            >
              <option value="">{staffBusy ? "Loading staff…" : "— Choose rider —"}</option>
              {staff.delivery.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {!staffBusy && staff.delivery.length === 0 && (
              <div style={{ fontSize: 11, color: BRAND.red, marginTop: 5, fontWeight: 600 }}>
                No active delivery staff found — add one under Staff first.
              </div>
            )}
          </div>

          <div style={{
            background: "var(--mly-surface-3)", color: "var(--mly-text)", borderRadius: 10,
            padding: "10px 12px", fontSize: 11.5, fontWeight: 600,
          }}>
            The rider then takes over from the MilliyO Rider app — pickup,
            delivered, failed and left-at-doorstep are never set here.
          </div>
        </ModalShell>
      )}

      {/* ═══ LIGHTWEIGHT STAGE CONFIRM ═══ */}
      {modal?.kind === "confirm" && (
        <ModalShell
          eyebrow={`${COLUMN_TITLE[modal.from]} → ${COLUMN_TITLE[modal.to] || modal.plan.stage}`}
          title={`Move to ${modal.plan.stage}?`}
          tone="pine"
          onClose={() => !saving && setModal(null)}
          footer={
            <>
              <button type="button" style={btnGhost} onClick={() => !saving && setModal(null)}>Cancel</button>
              <button
                type="button"
                autoFocus
                onClick={submitConfirm}
                disabled={saving}
                style={btnPrimary(saving)}
              >
                {saving ? "Saving…" : `Yes, ${modal.plan.stage}`}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 12.5, color: "var(--mly-text-soft)" }}>
            Order <strong style={{ color: "var(--mly-heading)" }}>{shortId(modal.order)}</strong> ·{" "}
            {modal.order.shipName || modal.order.customerName || "—"} ·{" "}
            {isPickupOrder(modal.order) ? "store pickup" : "home delivery"}
            <div style={{ marginTop: 8 }}>
              The customer is notified of the new stage. No staff assignment is needed for this step.
            </div>
          </div>

          {modalError && (
            <div style={{ background: "#FDECEA", color: "#C62828", padding: "9px 12px", borderRadius: 8, fontSize: 12, marginTop: 12 }}>
              {modalError}
            </div>
          )}
        </ModalShell>
      )}

      {/* ═══ CANCEL CONFIRM ═══ */}
      {modal?.kind === "cancel" && (
        <ModalShell
          eyebrow={`${COLUMN_TITLE[modal.from]} → Cancelled`}
          title="Cancel this order?"
          tone="danger"
          onClose={() => !saving && setModal(null)}
          footer={
            <>
              <button type="button" style={btnGhost} onClick={() => !saving && setModal(null)}>Keep order</button>
              <button
                type="button"
                autoFocus
                onClick={submitCancel}
                disabled={saving}
                style={btnPrimary(saving, BRAND.red)}
              >
                {saving ? "Cancelling…" : "Cancel order"}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 12.5, color: "var(--mly-text-soft)" }}>
            Order <strong style={{ color: "var(--mly-heading)" }}>{shortId(modal.order)}</strong> ·{" "}
            {modal.order.shipName || modal.order.customerName || "—"} · ₹{rupees(modal.order.totalPaise)}
          </div>
          <div style={{
            marginTop: 12, background: "#FDECEA", border: `1px solid ${BRAND.red}`,
            borderRadius: 10, padding: "10px 12px", fontSize: 12, color: "#8E3A2F", fontWeight: 600,
          }}>
            Cancelling releases everything this order was holding: shelf stock
            goes back to the packs it came from and the reserved grams of each
            milling slot are freed. The customer is notified and any redeemed
            Yo Points are returned. This cannot be undone.
          </div>

          {modalError && (
            <div style={{ background: "#FDECEA", color: "#C62828", padding: "9px 12px", borderRadius: 8, fontSize: 12, marginTop: 12 }}>
              {modalError}
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
};

export default PipelineBoard;
